<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  client: {
    type: Object,
    default: null,
  },
  uploading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'upload']);

const { t } = useI18n();

// File data
const selectedFiles = ref([]);
const monthMappings = ref({});
const isDragging = ref(false);

const monthOptions = computed(() => {
  const options = [];
  const currentYear = new Date().getFullYear();

  for (let year = currentYear; year <= currentYear + 1; year++) {
    for (let month = 1; month <= 12; month++) {
      const value = `${year}-${month.toString().padStart(2, '0')}`;
      const label = `${getMonthName(month)} ${year}`;
      options.push({ value, label });
    }
  }
  return options;
});

const getMonthName = month => {
  const names = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];
  return names[month - 1];
};

const getMonthShortName = month => {
  const names = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return names[month - 1];
};

// Format month value to display
const formatMonthDisplay = value => {
  if (!value) return null;
  const [year, month] = value.split('-');
  return `${getMonthShortName(parseInt(month))}/${year}`;
};

// Auto-detect month from filename
const detectMonthFromFilename = filename => {
  const lower = filename.toLowerCase();

  // Try to find year
  let year = null;
  const yearMatch = lower.match(/20(2[0-9]|30)/);
  if (yearMatch) {
    year = parseInt(yearMatch[0]);
  }

  // Try to find month by number
  let month = null;
  const monthNumMatch = lower.match(/[\s\-_\/.]0?([1-9]|1[0-2])[\s\-_\/.]/);
  if (monthNumMatch) {
    month = parseInt(monthNumMatch[1]);
  }

  // Try to find month by name
  if (!month) {
    const monthNames = {
      janeiro: 1,
      jan: 1,
      fevereiro: 2,
      fev: 2,
      marco: 3,
      março: 3,
      mar: 3,
      abril: 4,
      abr: 4,
      maio: 5,
      mai: 5,
      junho: 6,
      jun: 6,
      julho: 7,
      jul: 7,
      agosto: 8,
      ago: 8,
      setembro: 9,
      set: 9,
      outubro: 10,
      out: 10,
      novembro: 11,
      nov: 11,
      dezembro: 12,
      dez: 12,
    };

    for (const [name, num] of Object.entries(monthNames)) {
      if (lower.includes(name)) {
        month = num;
        break;
      }
    }
  }

  if (!month) return null;

  // Guess year if not found
  if (!year) {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    year =
      month < currentMonth
        ? currentDate.getFullYear() + 1
        : currentDate.getFullYear();
  }

  return `${year}-${month.toString().padStart(2, '0')}`;
};

const processFiles = files => {
  const pdfFiles = Array.from(files).filter(
    f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
  );

  pdfFiles.forEach(file => {
    if (!selectedFiles.value.find(f => f.name === file.name)) {
      selectedFiles.value.push(file);

      // Try to auto-detect month
      const detected = detectMonthFromFilename(file.name);
      if (detected) {
        monthMappings.value[file.name] = detected;
      }
    }
  });
};

const handleFileSelect = event => {
  processFiles(event.target.files);
  event.target.value = '';
};

const handleDrop = event => {
  event.preventDefault();
  isDragging.value = false;
  processFiles(event.dataTransfer.files);
};

const handleDragOver = event => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const removeFile = filename => {
  selectedFiles.value = selectedFiles.value.filter(f => f.name !== filename);
  delete monthMappings.value[filename];
};

const isValid = computed(() => {
  return (
    selectedFiles.value.length > 0 &&
    selectedFiles.value.every(f => monthMappings.value[f.name])
  );
});

const handleSubmit = () => {
  if (!isValid.value) return;

  emit('upload', {
    files: selectedFiles.value,
    monthMappings: monthMappings.value,
  });
};

// Reset when modal closes
watch(
  () => props.isOpen,
  newVal => {
    if (!newVal) {
      selectedFiles.value = [];
      monthMappings.value = {};
      isDragging.value = false;
    }
  }
);

// Get file status info
const getFileStatus = filename => {
  const detected = detectMonthFromFilename(filename);
  const current = monthMappings.value[filename];
  
  if (!detected && !current) {
    return { type: 'warning', icon: 'i-lucide-alert-triangle', label: t('OMNI.BOLETOS.UPLOAD_MODAL.STATUS.NEEDS_SELECTION') };
  }
  if (!detected && current) {
    return { type: 'manual', icon: 'i-lucide-pencil', label: t('OMNI.BOLETOS.UPLOAD_MODAL.STATUS.MANUAL') };
  }
  if (detected && current === detected) {
    return { type: 'auto', icon: 'i-lucide-sparkles', label: t('OMNI.BOLETOS.UPLOAD_MODAL.STATUS.AUTO_DETECTED') };
  }
  return { type: 'changed', icon: 'i-lucide-pencil', label: t('OMNI.BOLETOS.UPLOAD_MODAL.STATUS.CHANGED') };
};

// Stats
const statsInfo = computed(() => {
  const total = selectedFiles.value.length;
  const autoDetected = selectedFiles.value.filter(f => {
    const detected = detectMonthFromFilename(f.name);
    return detected && monthMappings.value[f.name] === detected;
  }).length;
  const needsAttention = selectedFiles.value.filter(f => !monthMappings.value[f.name]).length;
  
  return { total, autoDetected, needsAttention };
});
</script>

<template>
  <div
    v-if="isOpen && client"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-5 border-b border-n-weak bg-gradient-to-r from-woot-50 to-transparent dark:from-woot-900/20"
      >
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-woot-500 flex items-center justify-center text-white">
            <span class="i-lucide-upload text-2xl" />
          </div>
          <div>
            <h3 class="text-xl font-bold text-n-slate-12">
              {{ t('OMNI.BOLETOS.UPLOAD_MODAL.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 flex items-center gap-1">
              <span class="i-lucide-user size-3.5" />
              {{ client.contact.name }}
            </p>
          </div>
        </div>
        <button
          class="p-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-3 rounded-lg transition-colors"
          @click="emit('close')"
        >
          <span class="i-lucide-x text-xl" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto flex-1 space-y-5">
        <!-- Drop Zone -->
        <div
          class="border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200"
          :class="
            isDragging
              ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 scale-[1.02]'
              : 'border-n-weak hover:border-woot-400 hover:bg-n-alpha-2'
          "
          @drop="handleDrop"
          @dragover="handleDragOver"
          @dragleave="handleDragLeave"
        >
          <div class="w-16 h-16 rounded-2xl bg-n-alpha-3 flex items-center justify-center mx-auto mb-4">
            <span
              class="i-lucide-file-up text-3xl"
              :class="isDragging ? 'text-woot-500' : 'text-n-slate-10'"
            />
          </div>
          <p class="text-lg font-medium text-n-slate-12 mb-1">
            {{ t('OMNI.BOLETOS.UPLOAD_MODAL.DRAG_DROP') }}
          </p>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ t('OMNI.BOLETOS.UPLOAD_MODAL.OR') }}
          </p>
          <label
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-woot-500 text-white rounded-xl hover:bg-woot-600 cursor-pointer font-medium shadow-lg shadow-woot-500/20 transition-all hover:shadow-xl hover:shadow-woot-500/30"
          >
            <span class="i-lucide-folder-open" />
            {{ t('OMNI.BOLETOS.UPLOAD_MODAL.SELECT_FILES') }}
            <input
              type="file"
              accept=".pdf,application/pdf"
              multiple
              class="hidden"
              @change="handleFileSelect"
            />
          </label>
          <p class="text-xs text-slate-400 mt-4 flex items-center justify-center gap-1">
            <span class="i-lucide-info size-3" />
            {{ t('OMNI.BOLETOS.UPLOAD_MODAL.FORMAT_HINT') }}
          </p>
        </div>

        <!-- Info Banner (shown when files are selected) -->
        <div
          v-if="selectedFiles.length > 0"
          class="flex items-start gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800"
        >
          <span class="i-lucide-sparkles text-blue-500 text-lg flex-shrink-0 mt-0.5" />
          <div class="text-sm">
            <p class="font-medium text-blue-800 dark:text-blue-300">
              {{ t('OMNI.BOLETOS.UPLOAD_MODAL.AUTO_DETECT_INFO_TITLE') }}
            </p>
            <p class="text-blue-600 dark:text-blue-400 mt-0.5">
              {{ t('OMNI.BOLETOS.UPLOAD_MODAL.AUTO_DETECT_INFO_DESC') }}
            </p>
          </div>
        </div>

        <!-- Stats Bar -->
        <div
          v-if="selectedFiles.length > 0"
          class="flex items-center gap-4"
        >
          <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-alpha-3">
            <span class="i-lucide-files text-n-slate-10 size-4" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ statsInfo.total }} {{ t('OMNI.BOLETOS.UPLOAD_MODAL.FILES') }}
            </span>
          </div>
          <div v-if="statsInfo.autoDetected > 0" class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/30">
            <span class="i-lucide-sparkles text-green-500 size-4" />
            <span class="text-sm font-medium text-green-700 dark:text-green-300">
              {{ statsInfo.autoDetected }} {{ t('OMNI.BOLETOS.UPLOAD_MODAL.AUTO_DETECTED_COUNT') }}
            </span>
          </div>
          <div v-if="statsInfo.needsAttention > 0" class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/30">
            <span class="i-lucide-alert-triangle text-amber-500 size-4" />
            <span class="text-sm font-medium text-amber-700 dark:text-amber-300">
              {{ statsInfo.needsAttention }} {{ t('OMNI.BOLETOS.UPLOAD_MODAL.NEEDS_ATTENTION') }}
            </span>
          </div>
        </div>

        <!-- File Cards -->
        <div v-if="selectedFiles.length > 0" class="space-y-3">
          <div
            v-for="file in selectedFiles"
            :key="file.name"
            class="group relative bg-n-solid-3 border border-n-weak rounded-xl p-4 hover:shadow-md transition-shadow"
          >
            <div class="flex items-start gap-4">
              <!-- PDF Icon -->
              <div class="flex-shrink-0 w-12 h-14 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 flex items-center justify-center">
                <span class="i-lucide-file-text text-2xl text-red-500" />
              </div>

              <!-- File Info -->
              <div class="flex-1 min-w-0">
                <!-- Filename -->
                <div class="flex items-start justify-between gap-2 mb-2">
                  <div class="min-w-0">
                    <h4 class="text-sm font-semibold text-n-slate-12 truncate" :title="file.name">
                      {{ file.name }}
                    </h4>
                    <p class="text-xs text-n-slate-11 mt-0.5">
                      {{ (file.size / 1024).toFixed(1) }} KB
                    </p>
                  </div>
                  <button
                    class="p-1.5 text-n-slate-10 hover:text-red-500 bg-n-alpha-3 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all flex-shrink-0"
                    :title="t('OMNI.BOLETOS.DELETE')"
                    @click="removeFile(file.name)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>

                <!-- Month Selection -->
                <div class="flex items-center gap-3">
                  <!-- Status Badge -->
                  <div
                    class="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium"
                    :class="{
                      'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400': getFileStatus(file.name).type === 'auto',
                      'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400': getFileStatus(file.name).type === 'changed' || getFileStatus(file.name).type === 'manual',
                      'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400': getFileStatus(file.name).type === 'warning',
                    }"
                  >
                    <span :class="getFileStatus(file.name).icon" class="size-3" />
                    {{ getFileStatus(file.name).label }}
                  </div>

                  <!-- Month Selector -->
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-n-slate-11">{{ t('OMNI.BOLETOS.UPLOAD_MODAL.MONTH_LABEL') }}</span>
                    <select
                      v-model="monthMappings[file.name]"
                      class="px-3 py-1.5 text-sm font-medium border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 hover:border-woot-400 focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 transition-colors cursor-pointer"
                    >
                      <option :value="undefined" disabled>
                        {{ t('OMNI.BOLETOS.UPLOAD_MODAL.SELECT_MONTH') }}
                      </option>
                      <option
                        v-for="opt in monthOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="selectedFiles.length === 0"
          class="text-center py-8 text-n-slate-10"
        >
          <span class="i-lucide-file-question text-4xl mb-2 block" />
          <p>{{ t('OMNI.BOLETOS.UPLOAD_MODAL.NO_FILES_SELECTED') }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-between gap-4 px-6 py-4 border-t border-n-weak bg-n-alpha-2"
      >
        <p v-if="selectedFiles.length > 0 && !isValid" class="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <span class="i-lucide-alert-circle size-4" />
          {{ t('OMNI.BOLETOS.UPLOAD_MODAL.SELECT_ALL_MONTHS') }}
        </p>
        <div v-else />
        
        <div class="flex items-center gap-3">
          <button
            class="px-5 py-2.5 text-sm font-medium text-n-slate-12 bg-n-alpha-3 border border-n-weak rounded-xl hover:bg-n-slate-4 transition-colors"
            @click="emit('close')"
          >
            {{ t('OMNI.BOLETOS.CANCEL') }}
          </button>
          <button
            class="px-5 py-2.5 text-sm font-medium text-white bg-woot-500 rounded-xl hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-lg shadow-woot-500/20 transition-all"
            :disabled="!isValid || uploading"
            @click="handleSubmit"
          >
            <span v-if="uploading" class="i-lucide-loader-2 animate-spin" />
            <span v-else class="i-lucide-upload" />
            {{ t('OMNI.BOLETOS.UPLOAD_MODAL.UPLOAD') }} 
            <span v-if="selectedFiles.length > 0">({{ selectedFiles.length }})</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
